import { useMemo } from "react";
import type { Database } from "@/lib/supabase/supabase-types";

type FreelancerProfile = Database["public"]["Tables"]["freelancer_profiles"]["Row"];

export interface CompletionItem {
  id: string;
  label: string;
  completed: boolean;
  description?: string;
  route?: string;
  section?: string;
}

export interface ProfileCompletionResult {
  items: CompletionItem[];
  percentage: number;
  completedCount: number;
  totalCount: number;
  isComplete: boolean;
  nextIncompleteItem: CompletionItem | null;
}

interface CourseProgress {
  totalCourses: number;
  completedCourses: number;
  allCompleted: boolean;
}

/**
 * Hook to calculate profile completion status
 * @param profile - The freelancer profile data
 * @param courseProgress - Optional course progress data
 * @param portfolioCount - Number of portfolio projects
 * @param experienceCount - Number of work experience entries
 */
export function useProfileCompletion(
  profile: FreelancerProfile | null | undefined,
  courseProgress?: CourseProgress | null,
  portfolioCount: number = 0,
  experienceCount: number = 0
): ProfileCompletionResult {
  return useMemo(() => {
    // Extended profile type for new fields (some fields may not be in TypeScript types yet)
    const extendedProfile = profile as (FreelancerProfile & {
      video_intro_urls?: string[] | null;
      availability_status?: string | null;
      rate_range_min_cents?: number | null;
      rate_range_max_cents?: number | null;
    }) | null | undefined;

    // Define completion criteria - always return items even when profile is null
    const items: CompletionItem[] = [
      {
        id: "basic",
        label: "Basic Information",
        description: "Name, headline, location",
        completed: !!(
          profile?.display_name &&
          profile?.headline &&
          profile?.location
        ),
        route: "/genius/profile",
        section: "basic",
      },
      {
        id: "availability",
        label: "Availability",
        description: "Your current availability",
        completed: !!extendedProfile?.availability_status,
        route: "/genius/profile",
        section: "availability",
      },
      {
        id: "rate",
        label: "Rate Range",
        description: "Your hourly rate range",
        completed: !!(extendedProfile?.rate_range_min_cents || extendedProfile?.rate_range_max_cents),
        route: "/genius/profile",
        section: "rate",
      },
      {
        id: "languages",
        label: "Languages",
        description: "Languages you speak",
        completed:
          Array.isArray(profile?.languages) && (profile.languages as string[]).length >= 1,
        route: "/genius/profile",
        section: "languages",
      },
      {
        id: "bio",
        label: "Bio",
        description: "Tell us about yourself",
        completed: !!profile?.bio && profile.bio.trim().length > 0,
        route: "/genius/profile",
        section: "bio",
      },
      {
        id: "skills",
        label: "Skills & Expertise",
        description: "AI/ML focus areas",
        completed:
          Array.isArray(profile?.skills) && (profile.skills as string[]).length >= 1,
        route: "/genius/profile",
        section: "skills",
      },
      {
        id: "experience",
        label: "Add Work Experience",
        description: "Previous projects and roles",
        completed: experienceCount >= 1,
        route: "/genius/profile",
        section: "experience",
      },
      {
        id: "portfolio",
        label: "Upload Portfolio",
        description: "Showcase your best work",
        completed: portfolioCount >= 1,
        route: "/genius/profile",
        section: "portfolio",
      },
      {
        id: "training",
        label: "Complete Training",
        description: "Verify your background",
        completed: courseProgress?.allCompleted ?? false,
        route: "/genius/courses",
        section: "training",
      },
    ];

    const completedCount = items.filter((i) => i.completed).length;
    const percentage = Math.round((completedCount / items.length) * 100);
    const nextIncompleteItem = items.find((i) => !i.completed) || null;

    return {
      items,
      percentage,
      completedCount,
      totalCount: items.length,
      isComplete: completedCount === items.length,
      nextIncompleteItem,
    };
  }, [profile, courseProgress, portfolioCount, experienceCount]);
}

/**
 * Get the review status display info
 */
export function getReviewStatusInfo(status: string | null | undefined) {
  switch (status) {
    case "submitted":
      return {
        label: "Under Review",
        color: "amber" as const,
        description: "Your profile is being reviewed by our team",
        showTimeline: true,
      };
    case "approved":
      return {
        label: "Approved",
        color: "emerald" as const,
        description: "Your profile has been approved",
        showTimeline: false,
      };
    case "changes_requested":
      return {
        label: "Changes Requested",
        color: "amber" as const,
        description: "Please review the feedback and make updates",
        showTimeline: false,
      };
    case "rejected":
      return {
        label: "Rejected",
        color: "red" as const,
        description: "Your profile was not approved",
        showTimeline: false,
      };
    default:
      return {
        label: "Not Submitted",
        color: "gray" as const,
        description: "Complete your profile and submit for review",
        showTimeline: false,
      };
  }
}
